"use client"

import React, { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { TrendingUp } from "lucide-react"
import { Bar, BarChart, XAxis, YAxis } from "recharts"
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import { useOvertimeReasonsAnalytics } from "@/hooks/useOvertimeReasonsAnalytics"
import ExportButton from "@/components/ui/ExportButton"
import { TimeRange } from "@/types/enums"

interface OvertimeReasonsBarChartProps {
  className?: string
  startDate?: string
  endDate?: string
}

interface OvertimeReasonsBarChartHandle {
  download: () => void;
}

const OvertimeReasonsBarChart = forwardRef<OvertimeReasonsBarChartHandle, OvertimeReasonsBarChartProps>(
  function OvertimeReasonsBarChart({ className, startDate, endDate }, ref) {
    const [selectedMainTitle, setSelectedMainTitle] = useState<string>(TimeRange.ALL);
    const { reasonsAnalytics, isLoading } = useOvertimeReasonsAnalytics({ 
      startDate, 
      endDate 
    });

    // Get unique main titles for dropdown
    const mainTitles = useMemo(() => {
      if (!reasonsAnalytics?.length) return [];
      const unique = new Set<string>();
      reasonsAnalytics.forEach(reason => {
        if (reason.category?.trim()) {
          unique.add(reason.category.trim());
        }
      });
      return Array.from(unique).sort();
    }, [reasonsAnalytics]);

    // Filter reasons by selected main title and prepare chart data
    const chartData = useMemo(() => {
      if (!reasonsAnalytics?.length) return [];

      let filteredReasons = reasonsAnalytics;
      
      // Filter by selected main title
      if (selectedMainTitle !== TimeRange.ALL) {
        filteredReasons = reasonsAnalytics.filter(
          reason => reason.category === selectedMainTitle
        );
      }

      // Only show reasons with count > 0 and convert to chart data format
      return filteredReasons
        .filter(reason => reason.count > 0) // Only show reasons with actual usage
        .map((reason, index) => ({
          reason: reason.reason.length > 45 ? `${reason.reason.substring(0, 45)}...` : reason.reason,
          fullReason: reason.reason,
          count: reason.count,
          percentage: reason.percentage,
          category: reason.category,
          fill: `var(--color-chart-${(index % 5) + 1})`
        }))
        .sort((a, b) => b.count - a.count) // Sort by count descending
        .slice(0, 8); // Show top 8 reasons for better visibility
    }, [reasonsAnalytics, selectedMainTitle]);

    // Chart configuration
    const chartConfig = useMemo(() => {
      const config: ChartConfig = {
        count: {
          label: "Requests",
        },
      };
      
      chartData.forEach((item, index) => {
        const chartIndex = (index % 5) + 1;
        config[`chart-${chartIndex}`] = {
          label: item.fullReason,
          color: `hsl(var(--chart-${chartIndex}))`,
        };
      });
      
      return config;
    }, [chartData]);

    // Calculate trend data
    const trendData = useMemo(() => {
      if (!chartData.length) return null;
      
      const totalReasons = chartData.reduce((sum, item) => sum + item.count, 0);
      const topReason = chartData[0];
      const percentage = ((topReason.count / totalReasons) * 100).toFixed(1);
      
      return {
        topReason: topReason.fullReason,
        percentage,
        totalReasons
      };
    }, [chartData]);

    // Handle CSV download
    const handleDownload = () => {
      if (!chartData.length) return;

      const headers = ["Reason", "Count", "Main Title"];
      const csvData = chartData.map(item => [
        item.fullReason,
        item.count.toString(),
        selectedMainTitle === TimeRange.ALL ? "All Categories" : selectedMainTitle
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map(row => row.join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `overtime_reasons_analysis_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    useImperativeHandle(ref, () => ({
      download: handleDownload
    }));

    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex flex-col space-y-1">
            <CardTitle className="text-lg font-semibold">Overtime Reasons by Category</CardTitle>
            <CardDescription>Actual usage count of reasons within selected category</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              onClick={handleDownload} 
              disabled={!chartData.length || isLoading} 
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Main Title Dropdown Filter */}
          <div className="mb-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Category Filter
            </label>
            <Select
              value={selectedMainTitle}
              onValueChange={setSelectedMainTitle}
              disabled={isLoading || mainTitles.length === 0}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TimeRange.ALL}>All Categories</SelectItem>
                {mainTitles.map((title) => (
                  <SelectItem key={title} value={title}>
                    {title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Chart */}
          {isLoading ? (
            <div className="h-72 flex items-center justify-center">
              <div className="animate-pulse h-64 w-full bg-muted rounded-lg" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-72 flex flex-col items-center justify-center text-center text-muted-foreground">
              <TrendingUp className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No reasons data available</p>
              <p className="text-sm max-w-xs">
                {selectedMainTitle === TimeRange.ALL 
                  ? "No overtime reasons have been configured yet"
                  : "No reasons found for the selected category"
                }
              </p>
            </div>
          ) : (
            <ChartContainer config={chartConfig}>
              <BarChart
                accessibilityLayer
                data={chartData}
                layout="vertical"
                margin={{
                  left: 20,
                  right: 20,
                }}
                height={Math.max(300, chartData.length * 50)}
              >
                <YAxis
                  dataKey="reason"
                  type="category"
                  tickLine={false}
                  tickMargin={15}
                  axisLine={false}
                  width={180}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
                <XAxis dataKey="count" type="number" hide />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent hideLabel />}
                />
                <Bar dataKey="count" layout="vertical" radius={[0, 15, 15, 0]} />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
        {trendData && (
          <CardFooter className="flex-col items-start gap-2 text-sm">
            <div className="flex gap-2 leading-none font-medium">
              Most common: "{trendData.topReason}" ({trendData.percentage}%)
              <TrendingUp className="h-4 w-4" />
            </div>
            <div className="text-muted-foreground leading-none">
              Showing top {chartData.length} overtime reasons
              {selectedMainTitle !== TimeRange.ALL && ` in ${selectedMainTitle} category`}
            </div>
          </CardFooter>
        )}
      </Card>
    );
  }
);

export default OvertimeReasonsBarChart;
